import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, toDisplayString as _toDisplayString, withModifiers as _withModifiers, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, renderList as _renderList, Fragment as _Fragment, createTextVNode as _createTextVNode, createElementVNode as _createElementVNode, normalizeClass as _normalizeClass, resolveComponent as _resolveComponent, withCtx as _withCtx, createVNode as _createVNode, vShow as _vShow, withDirectives as _withDirectives } from "vue"

const _hoisted_1 = ["data-index-item-id"]
const _hoisted_2 = ["onClick"]
const _hoisted_3 = ["onClick"]
const _hoisted_4 = ["onClick"]
const _hoisted_5 = ["onClick"]
const _hoisted_6 = ["onClick"]
const _hoisted_7 = { key: 0 }
const _hoisted_8 = ["innerHTML"]
const _hoisted_9 = ["onClick"]
const _hoisted_10 = { key: 1 }
const _hoisted_11 = { key: 1 }
const _hoisted_12 = ["onClick"]
const _hoisted_13 = { key: 1 }

import {onUpdated, onMounted, ref, watch, onUnmounted} from 'vue';
import { trucodeSeeMoreLink, useSearchStore } from 'stores/search-store';
import { storeToRefs } from 'pinia';
import { useGrouperStore } from 'stores/grouper-store';
import { Services } from 'src/services/service-manager';
import { BookViewRecord, SearchService } from 'src/services/search-service';
import { isEmpty } from 'src/common/arrayUtil';
import debounce from 'lodash.debounce';
import { CodeType } from 'src/contracts/codeType';
import { HEADERS } from 'src/components/search/index-code-table-headers';
import {useResourcesStore} from "stores/resources-store";
import { DATA_BOOK_REGEX } from 'src/components/grouper-common/common-constants';
import {GrouperLayoutMode} from "src/services/IodineGrouperService";
import {useLayoutStore} from "stores/layout-store";

export interface IndexRecord {
  id: number | string;
  label: string;
  content?: SeeMoreContent[];
  children: IndexRecord[];
  item?: BookViewRecord;
}

type SeeMoreContent = string | {
  content: string,
  options: string
}

const SCROLL_THRESHOLD = 150,
      RECORD_INCREMENT = 50;


export default /*#__PURE__*/_defineComponent({
  __name: 'IndexList',
  setup(__props) {

const { layoutMode, searchSelected } = storeToRefs(useLayoutStore());
const treeRef = ref();
let minDataSequence: number, maxDataSequence: number;
const viewType = ref<string>();
const currentSubSearchIndex = ref<any | undefined>("");
const searchStore = useSearchStore();
const resourcesStore = useResourcesStore();

const { searchResultDetails, initialItemRecord, subSearchTerm, selectedIndexRecordID } = storeToRefs(searchStore);
const trucodeSearchService = Services.service(SearchService);
const grouperStore = useGrouperStore();

const expandedNodes = ref<Array<string | number>>([]);
let indexDiagnosisData = ref<IndexRecord[]>([]);
let mutableIndexData: IndexRecord[] = [];
let shouldScroll = false;

// This is entry point where click from the left search view performed
watch(
  searchResultDetails, (newSearchResults) => {
    parseNewResponse(newSearchResults);
  }
);

// This is point where click from the right sub search view performed
// We should scroll down to the selected node
watch(
  subSearchTerm, (term) => {
    scrollToHighlight(term);
  }
);

function parseNewResponse(newSearchResults: BookViewRecord[] | undefined) {
  mutableIndexData = [];
  expandedNodes.value = [];

  if (isEmpty(newSearchResults)) {
    return;
  }

  viewType.value = newSearchResults?.[0]?.data_book;
  // Refresh all data
  minDataSequence = initialItemRecord.value?.data_sequence || 0;
  maxDataSequence = initialItemRecord.value?.data_sequence || 0;

  if (viewType.value?.endsWith('INDEX')) {
    shouldScroll = true;
    parseResponseDiagnosisIndex(newSearchResults);
  }
}

onMounted(() => {
  if (!initialItemRecord.value) searchStore.focusIndexItem('A', undefined, true);
  if (!isEmpty(searchResultDetails.value))parseNewResponse(searchResultDetails.value)
  window.addEventListener('keydown', listKeyUp);
});

onUnmounted(() => {
  window.removeEventListener('keydown', listKeyUp);
});

// We should let data pass to vue and render, then perform scroll
onUpdated(() => {
  treeRef.value?.expandAll();
  if (shouldScroll) {
    setTimeout(() => {
      scrollToHighlight();
    });
    shouldScroll = false;
  }
});

function scrollToHighlight(term?: string) {
  if (!initialItemRecord.value) return;

  let id;
  if (term) {
    id = term;
    currentSubSearchIndex.value = Number(id);
  } else {
    id = `${initialItemRecord.value?.data_id ?? initialItemRecord.value?.text}`;
    searchStore.selectedIndexRecordID = '' + initialItemRecord.value?.data_id;
    searchStore.selectedIndexNode = '' + initialItemRecord.value?.text;
    if(searchStore.searchText) {
      currentSubSearchIndex.value = Number(id);
    }
  }
  selectedIndexRecordID.value = id;
  const xpath = `//div[@data-index-item-id="${id}"]`;
  const matchingElement = document.evaluate(
    xpath,
    treeRef.value?.$el,
    null,
    XPathResult.FIRST_ORDERED_NODE_TYPE,
    null
  ).singleNodeValue;
  if (!matchingElement && term) {
    // If there is no node available for specific id we should request new data for that id
    const dataId = Number(term);
    searchStore.getResultDetails({data_id: dataId, data_sequence: dataId, data_book: searchStore.getBookCode, data_date: grouperStore.patientEffectiveDate});
    searchStore.setSelectedSearchResult(dataId);
  } else {
    matchingElement?.scrollIntoView();
    const treeHolder = document.getElementById("tree-holder");
    if (treeHolder) {
      treeHolder.scrollTop = treeHolder.scrollTop - 5;
    }
    // view sometimes "jumps" when sub search happens because view might be scrollable, not convenient for UX
    // this is workaround to stick view to the left
    const elementsByClassNameElement = document.getElementsByClassName('search-result-details-pane');
    if (elementsByClassNameElement) {
      elementsByClassNameElement[0].scrollLeft = 0;
    }
  }
}

const onScrollDiagnosisIndex = debounce((event) => {
  const el = event.target;

  if (el.scrollTop + el.offsetHeight + 100 > treeRef.value?.$el?.clientHeight) {
    maxDataSequence += RECORD_INCREMENT;
    trucodeSearchService
      .getCodebooksDetails(
        viewType.value,
        maxDataSequence,
        useGrouperStore().patientEffectiveDate
      )
      .then((resultDetails) => {
        parseResponseDiagnosisIndex(resultDetails);
      });
  } else if (el.scrollTop <= SCROLL_THRESHOLD && minDataSequence > 50) {
    el.scrollTop = SCROLL_THRESHOLD + 1;
    minDataSequence -= RECORD_INCREMENT;
    trucodeSearchService
      .getCodebooksDetails(
        viewType.value,
        minDataSequence,
        useGrouperStore().patientEffectiveDate
      )
      .then((resultDetails) => {
        parseResponseDiagnosisIndex(resultDetails);
      });
  }
}, 100);

function parseResponseDiagnosisIndex(records: BookViewRecord[] | undefined) {
  if (records === undefined) {
    console.log('Something went wrong with response from trucode.');
    return;
  }
  for (const record of records) {
    addRecord(record);
  }
  sortIndexDiagnosisData(mutableIndexData);
  indexDiagnosisData.value = [...mutableIndexData];
}

function addRecord(record: BookViewRecord) {
  let nodeList = mutableIndexData;
  if (record.record.type === 'letter') {
    nodeList.push({
      id: record.data_id,
      label: record.record.content,
      children: [],
      item: record
    });
    return;
  } else if (record.record.full_path === null) {
    console.log('Record full path is null and its not letter type');
    console.dir(record);
    return;
  }

  const terms = record.record.full_path.full.split(';');
  // Root of the tree, parent node here

  for (let i = 0; i < terms.length; i++) {
    const term = terms[i];

    let node = nodeList.find((x) => x.label === term);

    if (!node) {
      node = {
        id: term, // TODO: this id doesn't really work since labels can be repeated
        children: [],
        label: term,
      };
      nodeList.push(node);
    }

    // last element is associated with the record
    if (i == terms.length - 1) {
      node.id = record.data_id;
      node.item = record;

      // parse off content links
      const partialResults = record.record?.content?.match(trucodeSeeMoreLink);
      if (partialResults && partialResults.length > 2) {

        const parts = term.split(partialResults[2]);
        const contentArray: SeeMoreContent[] = [];
        contentArray.push(parts[0]);
        contentArray.push({
          content: partialResults[2],
          options: partialResults[1]
        });
        if (parts[1] && parts[1].trim()) {
          contentArray.push(parts[1]);
        }
        node.content = contentArray;
      }
    }
    nodeList = node.children;
  }
}

// Sort logic
// if both elements have data id's, sort by those
// if one element has a data id and the other does not, the element without the
// data id appears first
// if both elements don't have a data id (shouldn't be possible but who knows), then
// fallback to sorting by label
function sortIndexDiagnosisData(children: IndexRecord[]) {
  children.sort(function (a: IndexRecord, b: IndexRecord) {
    if (typeof(a.id) === 'number' && typeof(b.id) === 'number') {
      return a.id - b.id;
    } else if (typeof(a.id) === 'number') {
      return 1;
    } else if (typeof(b.id) === 'number') {
      return -1;
    } else {
      const textA = a.label.toUpperCase();
      const textB = b.label.toUpperCase();
      return textA < textB ? -1 : textA > textB ? 1 : 0;
    }
  });
  for (const data of children) {
    if (data.children.length > 0) {
      sortIndexDiagnosisData(data.children);
    }
  }
}

async function codeClicked(item: IndexRecord, code: string) {
  const dataBook = item?.item?.data_book;
  if (dataBook) {
    switch (dataBook) {
      case 'ICD10CM_DX_INDEX':
      case 'ICD10CM_E_INDEX':
        handleDxCodeClick(item, code);
        break;
      case 'ICD10PCS_PR_INDEX':
        handleProcedureCodeClick(item, code);
        break;
    }
    await resourcesStore.getResearch(code, dataBook.match(DATA_BOOK_REGEX)[0]);
  }
}

async function handleDxCodeClick(item: IndexRecord, code: string) {
  code = code.replace('-', '');
  // check if code is addable
  if (!!await searchStore.quickAddCodeSearch(code, CodeType.Diagnosis)) {
    grouperStore.addDiagnosisCode(code, undefined, undefined, true);
  } else {
    searchStore.focusIndexItem(code, 'Ref', true);
  }
}

function handleProcedureCodeClick(item: IndexRecord, code: string) {
  // if code is 7 characters, then add to grouper directly
  if (code) {
    if (code.length == 7) {
      grouperStore.addProcedureCode(code, undefined, true);
    } else {
      searchStore.setSelectedProcedureCode(code);
    }
  }
}

function seeMoreClicked(item: SeeMoreContent, node: any) {
  searchStore.indexHistory.addToHistory(node.item);
  if (typeof item === 'string') {
    searchStore.focusIndexItem(item, undefined, true);
    searchStore.selectedIndexRecordID = '' + node.item?.data_id;
    searchStore.selectedIndexNode = node.label;
  } else {
    searchStore.focusIndexItem(item.content, item.options, true);
  }
}

function handleOpenCodeOrOpenTabular(e: any, code: string, node: any) {
  if(node.item.data_book === 'ICD10PCS_PR_INDEX') {
    codeClicked(node, code);
  } else {
    openTabular(e, code, node);
  }
}

function openTabular(e: any, code: string, node: any) {
  if(node) {
    if (code) {
      e.preventDefault;
      searchStore.indexHistory.addToHistory(node.item);
      searchStore.focusIndexItem(code, 'Ref', true);
    } else {
      searchStore.selectedIndexRecordID = '' + node.item?.data_id;
      searchStore.selectedIndexNode = node.label;
      currentSubSearchIndex.value = node.id
    }
  }
}

function makeNodeSelected(node: any) {
  searchStore.clearSubSearch = true;
  currentSubSearchIndex.value = node.id
  searchStore.selectedIndexRecordID = '' + node.id
}

function listKeyUp(event: KeyboardEvent) {
  if (activeElementInSearchArea() && ['ArrowUp', 'ArrowDown', 'Enter', 'Tab'].includes(event.key)
      && (viewType.value === 'ICD10CM_DX_INDEX' || viewType.value === 'ICD10PCS_PR_INDEX' || viewType.value === 'ICD10CM_E_INDEX')
      && !activeElementInSearchBlock()) {
    event.preventDefault();

    const nodes = treeRef.value.nodes;
    const selectedElement = treeRef.value.selected;
    const firstElement = nodes.map(item => findFirstElement(item)).find(itemId => !!itemId)
    const lastElement = findLastElement(nodes.at(-1))
    const scrollableContainer = document.getElementById("tree-holder");

    if (event.key === 'Enter') {
      const selectedNode = treeRef.value.getNodeByKey(selectedElement);
      if(selectedNode?.content) {
        const content = findClickableContent(selectedNode.content).content;
        seeMoreClicked(content, selectedNode);
      } else {
        handleOpenCodeOrOpenTabular({}, selectedNode?.item?.record?.regular_row_codes?.[0] ?? '', selectedNode)
      }
    } else if (event.key === 'ArrowDown' || event.key === 'Tab') {
      if (lastElement > selectedElement) {
        const xpath = `//div[@data-index-item-id="${selectedElement + 1}"]`;
        const matchingElement = document.evaluate(
            xpath,
            treeRef.value?.$el,
            null,
            XPathResult.FIRST_ORDERED_NODE_TYPE,
            null
        ).singleNodeValue

        if(!isInScrollableViewport(matchingElement, scrollableContainer)) {
          matchingElement?.scrollIntoView();
          scrollableContainer.scrollTop = scrollableContainer.scrollTop - 5;
        }
        makeNodeSelected({id: selectedElement + 1, label: 'N/A', item: {data_id: selectedElement + 1}})
      }
    } else if (event.key === 'ArrowUp') {
      if (selectedElement > firstElement) {
        const xpath = `//div[@data-index-item-id="${selectedElement -1}"]`;
        const matchingElement = document.evaluate(
            xpath,
            treeRef.value?.$el,
            null,
            XPathResult.FIRST_ORDERED_NODE_TYPE,
            null
        ).singleNodeValue
        if(!isInScrollableViewport(matchingElement, scrollableContainer)) {
          matchingElement?.scrollIntoView();
          scrollableContainer.scrollTop = scrollableContainer.scrollTop - 5;
        }
        makeNodeSelected({id: selectedElement - 1, label: 'N/A', item: {data_id: selectedElement + 1}})
      }
    }
  }
}

function findClickableContent(contents) {
  return contents.find(item => !isString(item))
}

function isString(value) {
  return typeof value === 'string' || value instanceof String;
}

function findLastElement(node) {
  if(node.children.length > 0) {
    return findLastElement(node.children.at(-1))
  } else {
    return node.id;
  }
}

function findFirstElement(node) {
  if(Number.isFinite(node.id)) {
    return node.id;
  } else if(node.children.length > 0) {
    return node.children.map(item => findFirstElement(item)).find(itemId => !!itemId)
  } else {
    return undefined;
  }
}

function isInScrollableViewport(element, scrollableContainer) {
  if(!element) {
    return true;
  }

  const elementRect = element.getBoundingClientRect();
  const containerRect = scrollableContainer.getBoundingClientRect();

  return (
      elementRect.top >= containerRect.top &&
      elementRect.left >= containerRect.left &&
      elementRect.bottom <= containerRect.bottom &&
      elementRect.right <= containerRect.right
  );
}

function activeElementInSearchBlock() {
  return !!document.activeElement.closest(".search-results-header") || !!document.activeElement.closest(".sub-search-holder")
}

function activeElementInSearchArea() {
  return searchSelected.value && (!!document.activeElement.closest(".search-panel-area") || document.activeElement.classList.contains("grouper-fullscreen-wrapper"))
}


return (_ctx: any,_cache: any) => {
  const _component_q_tree = _resolveComponent("q-tree")!

  return _withDirectives((_openBlock(), _createElementBlock("div", {
    id: "tree-holder",
    class: "iodine-scrollable-y browse-search full-height",
    onScroll: _cache[1] || (_cache[1] = 
//@ts-ignore
(...args) => (_unref(onScrollDiagnosisIndex) && _unref(onScrollDiagnosisIndex)(...args)))
  }, [
    _createVNode(_component_q_tree, {
      nodes: _unref(indexDiagnosisData),
      "node-key": "id",
      "no-connectors": "",
      icon: "fa-solid fa-caret-right",
      class: _normalizeClass(["iodine-padding-left-lg", {'index-list-embedded-wrapper': _unref(layoutMode) == _unref(GrouperLayoutMode).fullScreen}]),
      "default-expand-all": true,
      "no-transition": true,
      selected: currentSubSearchIndex.value,
      "onUpdate:selected": _cache[0] || (_cache[0] = ($event: any) => ((currentSubSearchIndex).value = $event)),
      ref_key: "treeRef",
      ref: treeRef
    }, {
      "default-header": _withCtx((prop) => [
        _createElementVNode("div", {
          class: _normalizeClass(["row items-center iodine-padding-left-sm", {'io-mfe-ig-index-list-item-letter': prop?.node?.item?.record?.type == 'letter',
                      'io-mfe-ig-index-list-item-table-codes': prop?.node?.item?.record?.type == 'tablerow' || prop?.node?.item?.record?.type == 'tablerowchannelpublishing',
                      'sub-search-current-term': prop.node.id === currentSubSearchIndex.value}]),
          "data-index-item-id": prop.node.id || prop.node.label
        }, [
          _createElementVNode("div", {
            class: "io-mfe-ig-index-list-item-desc",
            onClick: _withModifiers(() => makeNodeSelected(prop.node), ["stop"])
          }, [
            (!prop.node.content)
              ? (_openBlock(), _createElementBlock("span", {
                  key: 0,
                  onClick: _withModifiers((e) => openTabular(e, prop?.node?.item?.record?.regular_row_codes?.[0], prop?.node), ["stop"])
                }, _toDisplayString(prop.node.label), 9, _hoisted_3))
              : (_openBlock(), _createElementBlock("span", {
                  key: 1,
                  onClick: _withModifiers((e) => openTabular(e, prop?.node?.item?.record?.regular_row_codes?.[0], prop?.node), ["stop"])
                }, [
                  (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(prop.node.content, (content, index) => {
                    return (_openBlock(), _createElementBlock(_Fragment, { key: index }, [
                      (typeof content === 'string')
                        ? (_openBlock(), _createElementBlock(_Fragment, { key: 0 }, [
                            _createTextVNode(_toDisplayString(content), 1)
                          ], 64))
                        : (_openBlock(), _createElementBlock("a", {
                            key: 1,
                            class: "io-mfe-ig-index-list-item-link",
                            onClick: _withModifiers(($event: any) => (seeMoreClicked(content, prop?.node)), ["stop"])
                          }, _toDisplayString(content.content), 9, _hoisted_5))
                    ], 64))
                  }), 128))
                ], 8, _hoisted_4)),
            (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(prop?.node?.item?.record?.regular_row_codes, (code) => {
              return (_openBlock(), _createElementBlock("a", {
                key: `${prop?.node?.id}-${code}`,
                class: "io-mfe-ig-index-list-item-code-link",
                onClick: _withModifiers((e) => handleOpenCodeOrOpenTabular(e, code, prop.node), ["stop"])
              }, _toDisplayString(code), 9, _hoisted_6))
            }), 128))
          ], 8, _hoisted_2),
          (prop?.node?.item?.record?.table_row_codes?.items
          && prop?.node?.id == _unref(initialItemRecord)?.data_sequence
          && _unref(HEADERS)[prop?.node?.item?.record?.table_row_codes?.table])
            ? (_openBlock(), _createElementBlock("table", _hoisted_7, [
                _createElementVNode("thead", null, [
                  _createElementVNode("tr", null, [
                    (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(_unref(HEADERS)[prop?.node?.item?.record?.table_row_codes?.table], (header, index) => {
                      return (_openBlock(), _createElementBlock("th", {
                        key: index,
                        innerHTML: header
                      }, null, 8, _hoisted_8))
                    }), 128))
                  ])
                ]),
                _createElementVNode("tbody", null, [
                  _createElementVNode("tr", null, [
                    (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(prop?.node?.item?.record?.table_row_codes?.items, (code, index) => {
                      return (_openBlock(), _createElementBlock("td", {
                        key: `${prop?.node?.id}-table-${index}`
                      }, [
                        (code?.[0])
                          ? (_openBlock(), _createElementBlock("a", {
                              key: 0,
                              class: "io-mfe-ig-index-list-item-code-link",
                              onClick: _withModifiers(($event: any) => (codeClicked(prop.node, code?.[0])), ["stop"])
                            }, _toDisplayString(code?.[0]), 9, _hoisted_9))
                          : (_openBlock(), _createElementBlock("span", _hoisted_10, "--"))
                      ]))
                    }), 128))
                  ])
                ])
              ]))
            : (_openBlock(), _createElementBlock("table", _hoisted_11, [
                _createElementVNode("tbody", null, [
                  _createElementVNode("tr", null, [
                    (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(prop?.node?.item?.record?.table_row_codes?.items, (code, index) => {
                      return (_openBlock(), _createElementBlock("td", {
                        key: `${prop?.node?.id}-table-${index}`
                      }, [
                        (code?.[0])
                          ? (_openBlock(), _createElementBlock("a", {
                              key: 0,
                              class: "io-mfe-ig-index-list-item-code-link",
                              onClick: _withModifiers(($event: any) => (codeClicked(prop.node, code?.[0])), ["stop"])
                            }, _toDisplayString(code?.[0]), 9, _hoisted_12))
                          : (_openBlock(), _createElementBlock("span", _hoisted_13, "--"))
                      ]))
                    }), 128))
                  ])
                ])
              ]))
        ], 10, _hoisted_1)
      ]),
      _: 1
    }, 8, ["nodes", "class", "selected"])
  ], 544)), [
    [_vShow, viewType.value === 'ICD10CM_DX_INDEX' || viewType.value === 'ICD10PCS_PR_INDEX' || viewType.value === 'ICD10CM_E_INDEX']
  ])
}
}

})